import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Grid, 
  Card, 
  CircularProgress, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip
} from "@mui/material";
import { 
  TrendingUp, 
  History, 
  Refresh,
  TrendingDown,
  AccountBalance
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import OrderService from "services/orderService";
import axiosInstance from "utils/axios";

// Custom Tab Panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function AccountDetails() {
  const { accountName } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [trades, setTrades] = useState([]);
  const [openPositions, setOpenPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMarketPair, setSelectedMarketPair] = useState("");

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Fetch open positions for this account
  const fetchOpenPositions = async () => {
    try {
      console.log('🔍 Fetching open positions for account:', accountName);
      const response = await axiosInstance.get(`/api/v1/trades/open-positions/${accountName}`);
      console.log('✅ Open positions response:', response.data);
      setOpenPositions(response.data);
    } catch (error) {
      console.error('❌ Error fetching open positions:', error);
      setOpenPositions([]);
    }
  };

  // Refresh data
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchOpenPositions(),
      fetchTrades()
    ]);
    setRefreshing(false);
  };

  // Fetch trades function
  const fetchTrades = async () => {
    try {
      const tradeData = await OrderService.getTradesByAccountName(accountName);
      setTrades(
        (tradeData || []).sort(
          (a, b) => new Date(b.created_time) - new Date(a.created_time)
        )
      );
    } catch (error) {
      console.error("Error fetching trades:", error);
      setTrades([]);
    }
  };

  useEffect(() => {
    if (!accountName) {
      console.warn("Missing accountName in URL, redirecting...");
      navigate("/accounts");
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('🚀 Loading data for account:', accountName);
        await Promise.all([fetchOpenPositions(), fetchTrades()]);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [accountName, navigate]);

  // Open Positions table columns
  const openPositionsColumns = [
    { Header: "Symbol", accessor: "symbol", width: "15%" },
    { Header: "Side", accessor: "side", width: "10%" },
    { Header: "Size", accessor: "size", width: "12%" },
    { Header: "Entry Price", accessor: "entry_price", width: "12%" },
    { Header: "Mark Price", accessor: "mark_price", width: "12%" },
    { Header: "Unrealized PnL", accessor: "unrealized_pnl", width: "15%" },
    { Header: "Opened", accessor: "created_time", width: "12%" },
  ];

  // Format open positions data for table
  const formatOpenPositionsData = (positions) => {
    return positions.map((position, index) => ({
      symbol: (
        <MDTypography variant="button" fontWeight="medium" color="text">
          {position.symbol}
        </MDTypography>
      ),
      side: (
        <Chip
          label={position.side}
          color={position.side === 'Buy' ? 'success' : 'error'}
          size="small"
          icon={position.side === 'Buy' ? <TrendingUp /> : <TrendingDown />}
        />
      ),
      size: (
        <MDTypography variant="button" fontWeight="medium" color="text">
          {position.size.toFixed(4)}
        </MDTypography>
      ),
      entry_price: (
        <MDTypography variant="button" fontWeight="medium" color="text">
          ${position.entry_price.toFixed(2)}
        </MDTypography>
      ),
      mark_price: (
        <MDTypography variant="button" fontWeight="medium" color="text">
          ${position.mark_price.toFixed(2)}
        </MDTypography>
      ),
      unrealized_pnl: (
        <MDBox display="flex" alignItems="center" gap={1}>
          <MDTypography 
            variant="button" 
            fontWeight="medium" 
            color={position.unrealized_pnl >= 0 ? "success" : "error"}
          >
            ${position.unrealized_pnl.toFixed(2)}
          </MDTypography>
          <MDTypography 
            variant="caption" 
            color={position.unrealized_pnl_percentage >= 0 ? "success" : "error"}
          >
            ({position.unrealized_pnl_percentage.toFixed(2)}%)
          </MDTypography>
        </MDBox>
      ),
      created_time: (
        <MDTypography variant="button" fontWeight="medium" color="text">
          {new Date(position.created_time).toLocaleString()}
        </MDTypography>
      ),
    }));
  };

  // Get unique market pairs from trades
  const marketPairs = useMemo(() => {
    const pairs = [...new Set(trades.map(trade => trade.symbol).filter(Boolean))];
    return pairs.sort();
  }, [trades]);

  // Filter trades based on selected market pair
  const filteredTrades = useMemo(() => {
    if (!selectedMarketPair) {
      return trades;
    }
    return trades.filter(trade => trade.symbol === selectedMarketPair);
  }, [trades, selectedMarketPair]);

  const tableData = useMemo(() => {
    return {
      columns: [
        { Header: "Date", accessor: "date", align: "left", width: "18%" },
        { Header: "Market pair", accessor: "market_pair", align: "left", width: "15%" },
        { Header: "Side", accessor: "side", align: "left", width: "10%" },
        { Header: "Type", accessor: "type", align: "left", width: "10%" },
        { Header: "Price", accessor: "price", align: "right", width: "12%" },
        { Header: "Amount", accessor: "amount", align: "right", width: "12%" },
        { Header: "Executed", accessor: "executed", align: "right", width: "11%" },
        { Header: "Status", accessor: "status", align: "center", width: "12%" },
      ],
      rows: filteredTrades.map((trade) => ({
        date: (
          <MDTypography variant="caption" fontWeight="medium">
            {trade.created_time ? new Date(trade.created_time).toLocaleString() : "N/A"}
          </MDTypography>
        ),
        market_pair: (
          <MDTypography variant="caption" fontWeight="medium">
            {trade.symbol || "—"}
          </MDTypography>
        ),
        side: (
          <MDTypography
            variant="caption"
            color={
              trade.side?.toLowerCase() === "buy"
                ? "success"
                : trade.side?.toLowerCase() === "sell"
                ? "error"
                : "text"
            }
            fontWeight="medium"
          >
            {trade.side || "—"}
          </MDTypography>
        ),
        type: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {trade.order_type || "—"}
          </MDTypography>
        ),
        price: (
          <MDTypography variant="caption" fontWeight="medium" align="right">
            ≈{trade.price ?? "—"}
          </MDTypography>
        ),
        amount: (
          <MDTypography variant="caption" fontWeight="medium" align="right">
            {trade.qty ?? "—"}
          </MDTypography>
        ),
        executed: (
          <MDTypography variant="caption" fontWeight="medium" align="right">
            {trade.cum_exec_qty ?? "—"}/{trade.qty ?? "—"}
          </MDTypography>
        ),
        status: (
          <MDTypography
            variant="caption"
            color={trade.status === "open" ? "warning" : "text"}
            fontWeight="medium"
            align="center"
          >
            {trade.status || "—"}
          </MDTypography>
        ),
      })),
    };
  }, [filteredTrades]);

  const handleMarketPairChange = (event) => {
    setSelectedMarketPair(event.target.value);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  {accountName} - Trading Dashboard
                </MDTypography>
                <Tooltip title="Refresh Data">
                  <IconButton 
                    color="inherit" 
                    onClick={handleRefresh}
                    disabled={refreshing}
                  >
                    {refreshing ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                  </IconButton>
                </Tooltip>
              </MDBox>
              
              <MDBox sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  aria-label="account tabs"
                  sx={{ px: 2 }}
                >
                  <Tab 
                    label={
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <TrendingUp />
                        <span>Open Positions ({openPositions.length})</span>
                      </MDBox>
                    } 
                  />
                  <Tab 
                    label={
                      <MDBox display="flex" alignItems="center" gap={1}>
                        <History />
                        <span>Trade History ({trades.length})</span>
                      </MDBox>
                    } 
                  />
                </Tabs>
              </MDBox>

              <TabPanel value={tabValue} index={0}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ 
                      columns: openPositionsColumns, 
                      rows: formatOpenPositionsData(openPositions) 
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <MDBox sx={{ overflowX: "auto" }}>
                    <DataTable
                      table={tableData}
                      isSorted={false}
                      entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25] }}
                      showTotalEntries={true}
                      noEndBorder
                      canSearch={false}
                      customHeader={
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel id="market-pair-filter-label">Market Pair</InputLabel>
                          <Select
                            labelId="market-pair-filter-label"
                            id="market-pair-filter"
                            value={selectedMarketPair}
                            label="Market Pair"
                            onChange={handleMarketPairChange}
                            sx={{
                              height: 40,
                              "& .MuiSelect-select": {
                                paddingTop: "8px",
                                paddingBottom: "8px",
                                lineHeight: "24px"
                              }
                            }}
                          >
                            <MenuItem value="">
                              <em>All Market Pairs</em>
                            </MenuItem>
                            {marketPairs.map((pair) => (
                              <MenuItem key={pair} value={pair}>
                                {pair}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      }
                    />
                  </MDBox>
                )}
              </TabPanel>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AccountDetails;
